import { useEffect, useState } from "react";
import {
	Alert,
	Button,
	Divider,
	LinearProgress,
	Stack,
	TableBody,
	TableCell,
	TableRow,
	Typography,
} from "@mui/material";
import type {
	RollAssignmentDetail,
	RollAssignmentHistory as History,
} from "./models";
import { getRollAssignmentHistory } from "./repository";

function AssignmentList({
	side,
	name,
	assignments,
}: {
	side: "Rollshop" | "MES";
	name: string | null;
	assignments: RollAssignmentDetail[];
}) {
	const currentOnly = side === "Rollshop";
	return (
		<Stack gap={1} aria-label={`${side} roll assignments`}>
			<Typography variant="body2" fontWeight={600}>
				{side} · {currentOnly ? "Current assignments" : "Latest 3 assignments"}
			</Typography>
			{currentOnly && (
				<Typography variant="caption" color="text.secondary">
					Current slots only. Assignment dates are unavailable.
				</Typography>
			)}
			{assignments.length === 0 ? (
				<Typography variant="body2" color="text.secondary">
					{!name
						? "Roll not present."
						: currentOnly
							? "No current assignments."
							: "No recorded assignments."}
				</Typography>
			) : (
				<Stack divider={<Divider />} gap={1}>
					{assignments.map((assignment) => (
						<Stack key={assignment.id} gap={0.5}>
							<Typography variant="body2" fontWeight={600}>
								{assignment.rollset ?? "Unknown rollset"} ·{" "}
								{assignment.position ?? "Unknown position"}
							</Typography>
							{!currentOnly && (
								<>
									<Typography variant="body2">
										Assigned: {assignment.assignedDate ?? "Date unavailable"}
									</Typography>
									<Typography variant="body2">
										Removed: {assignment.removedDate ?? "Still open"}
									</Typography>
									{assignment.removeStatus && (
										<Typography variant="body2">
											Status after removal: {assignment.removeStatus}
										</Typography>
									)}
									{assignment.removeReason && (
										<Typography variant="body2">
											Reason: {assignment.removeReason}
										</Typography>
									)}
								</>
							)}
							<Typography variant="caption" color="text.secondary">
								{assignment.currentSlot
									? "Roll currently in this slot."
									: "Roll not currently in this slot."}
							</Typography>
						</Stack>
					))}
				</Stack>
			)}
		</Stack>
	);
}

export function RollAssignmentHistory({
	rollshopName,
	mesName,
}: {
	rollshopName: string | null;
	mesName: string | null;
}) {
	const [history, setHistory] = useState<History | null>(null);
	const [error, setError] = useState("");
	const [attempt, setAttempt] = useState(0);
	useEffect(() => {
		const controller = new AbortController();
		setHistory(null);
		setError("");
		getRollAssignmentHistory(rollshopName, mesName, controller.signal)
			.then((data) => {
				if (!controller.signal.aborted) setHistory(data);
			})
			.catch((reason) => {
				if (!controller.signal.aborted)
					setError(
						reason instanceof Error
							? reason.message
							: "Could not load roll assignments.",
					);
			});
		return () => controller.abort();
	}, [rollshopName, mesName, attempt]);
	return (
		<TableBody>
			<TableRow
				sx={{
					"& > td": {
						verticalAlign: "top",
						pt: 3,
						borderTop: 2,
						borderColor: "divider",
					},
				}}
			>
				<TableCell>
					<Typography variant="subtitle2">Roll assignments</Typography>
					<Typography variant="caption" color="text.secondary">
						MES history is shown newest first, across all dates.
					</Typography>
				</TableCell>
				{history ? (
					<>
						<TableCell>
							<AssignmentList
								side="Rollshop"
								name={rollshopName}
								assignments={history.rollshop}
							/>
						</TableCell>
						<TableCell>
							<AssignmentList
								side="MES"
								name={mesName}
								assignments={history.mes}
							/>
						</TableCell>
						<TableCell>
							<Typography variant="caption" color="text.secondary">
								View only
							</Typography>
						</TableCell>
					</>
				) : (
					<TableCell colSpan={3}>
						{error ? (
							<Alert
								severity="warning"
								action={
									<Button onClick={() => setAttempt((value) => value + 1)}>
										Retry
									</Button>
								}
							>
								{error}
							</Alert>
						) : (
							<Stack gap={1} role="status">
								<Typography variant="body2">
									Loading roll assignments…
								</Typography>
								<LinearProgress aria-label="Loading roll assignments" />
							</Stack>
						)}
					</TableCell>
				)}
			</TableRow>
		</TableBody>
	);
}
